import React, { useState } from 'react';
import { useTable, useSortBy, useFilters } from 'react-table';
import { toast } from 'react-toastify';
import axios from 'axios';

// Custom filter component for text filtering
function DefaultColumnFilter({
  column: { filterValue, setFilter, Header }
}) {
  return (
    <input
      className="shadow appearance-none border rounded w-full py-1 px-2 text-gray-700 text-sm leading-tight focus:outline-none focus:shadow-outline"
      value={filterValue || ''}
      onChange={e => setFilter(e.target.value || undefined)}
      placeholder={`Search ${Header.toLowerCase()}`}
    />
  );
}

export default function AlertList({ alerts, onEdit, onView, onRefresh }) {
  const [activeFilter, setActiveFilter] = useState(null);

  const handleRunAlert = async (alertId) => {
    try {
      await axios.post(`/api/alerts/${alertId}/run`);
      toast.success('Alert execution triggered successfully!');
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Error running alert:', error);
      const errorMessage = error.response?.data?.detail || 'An error occurred while running the alert';
      toast.error(errorMessage);
    }
  };

  const handleToggleActive = async (alert) => {
    try {
      await axios.put(`/api/alerts/${alert.id}`, { is_active: !alert.is_active });
      toast.success(`Alert ${!alert.is_active ? 'activated' : 'deactivated'} successfully!`);
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Error toggling alert status:', error);
      const errorMessage = error.response?.data?.detail || 'An error occurred while updating the alert';
      toast.error(errorMessage);
    }
  };

  const handleDeleteAlert = async (alertId) => {
    if (!window.confirm('Are you sure you want to delete this alert?')) return;
    
    try {
      await axios.delete(`/api/alerts/${alertId}`);
      toast.success('Alert deleted successfully!');
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Error deleting alert:', error);
      const errorMessage = error.response?.data?.detail || 'An error occurred while deleting the alert';
      toast.error(errorMessage);
    }
  };

  const columns = React.useMemo(
    () => [
      {
        Header: 'ID',
        accessor: 'id',
        Cell: ({ value }) => <span className="text-gray-600 font-mono">{value}</span>
      },
      {
        Header: 'Name',
        accessor: 'name',
        Cell: ({ value, row }) => (
          <button 
            className="text-blue-500 hover:text-gray-800 font-medium"
            onClick={() => onView(row.original)}
          >
            {value}
          </button>
        )
      },
      {
        Header: 'Node Type',
        accessor: 'node_type'
      },
      {
        Header: 'Query Type',
        accessor: 'query_type',
        Cell: ({ value }) => value.toUpperCase()
      },
      {
        Header: 'Schedule',
        accessor: 'schedule_type',
        Cell: ({ value }) => value.charAt(0).toUpperCase() + value.slice(1)
      },
      {
        Header: 'Last Run',
        accessor: 'last_run_date',
        Cell: ({ value }) => value ? new Date(value).toLocaleString() : 'Never'
      },
      {
        Header: 'Status',
        accessor: 'is_active',
        Cell: ({ value }) => (
          <span className={`px-2 py-1 rounded text-xs font-medium ${value ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
            {value ? 'Active' : 'Inactive'}
          </span>
        ),
        Filter: ({ column }) => (
          <select
            className="shadow border rounded w-full py-1 px-2 text-gray-700 text-sm leading-tight focus:outline-none focus:shadow-outline"
            value={column.filterValue || ''}
            onChange={e => {
              const value = e.target.value;
              column.setFilter(value === '' ? undefined : value === 'true');
              setActiveFilter(value === '' ? null : value === 'true');
            }}
          >
            <option value="">All</option>
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </select>
        )
      },
      {
        Header: 'Actions',
        id: 'actions',
        Cell: ({ row }) => (
          <div className="flex space-x-2">
            <button
              onClick={() => handleRunAlert(row.original.id)}
              className="bg-purple-l hover:bg-green-700 text-white text-xs font-bold py-1 px-2 rounded focus:outline-none focus:shadow-outline"
              title="Run Alert"
            >
              Run
            </button>
            <button
              onClick={() => onEdit(row.original)}
              className="bg-purple-l hover:bg-blue-700 text-white text-xs font-bold py-1 px-2 rounded focus:outline-none focus:shadow-outline"
              title="Edit Alert"
            >
              Edit
            </button>
            <button
              onClick={() => handleToggleActive(row.original)}
              className={`${row.original.is_active ? 'bg-yellow-500 hover:bg-yellow-700' : 'bg-purple-500 hover:bg-purple-700'} text-white text-xs font-bold py-1 px-2 rounded focus:outline-none focus:shadow-outline`}
              title={row.original.is_active ? 'Deactivate Alert' : 'Activate Alert'}
            >
              {row.original.is_active ? 'Disable' : 'Enable'}
            </button>
            <button
              onClick={() => handleDeleteAlert(row.original.id)}
              className="bg-purple-l hover:bg-red-700 text-white text-xs font-bold py-1 px-2 rounded focus:outline-none focus:shadow-outline"
              title="Delete Alert"
            >
              Delete
            </button>
          </div>
        )
      }
    ],
    [onEdit, onView, onRefresh]
  );

  const defaultColumn = React.useMemo(
    () => ({
      Filter: DefaultColumnFilter,
    }),
    []
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
  } = useTable(
    {
      columns,
      data: alerts,
      defaultColumn,
      initialState: {
        filters: activeFilter !== null ? [{ id: 'is_active', value: activeFilter }] : []
      }
    },
    useFilters,
    useSortBy
  );

  return (
    <div className="bg-white shadow rounded">
      <h2 className="bg-purple-l border-b border-gray-200 px-6 py-2 text-white font-bold tracking-wider uppercase text-md">
        Alert Definitions
      </h2>
      
      <div className="overflow-x-auto">
        <table {...getTableProps()} className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            {headerGroups.map(headerGroup => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map(column => (
                  <th
                    {...column.getHeaderProps(column.getSortByToggleProps())}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    <div className="flex items-center">
                      {column.render('Header')}
                      <span>
                        {column.isSorted
                          ? column.isSortedDesc
                            ? ' 🔽'
                            : ' 🔼'
                          : ''}
                      </span>
                    </div>
                    <div>{column.canFilter ? column.render('Filter') : null}</div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()} className="bg-white divide-y divide-gray-200">
            {rows.length > 0 ? (
              rows.map(row => {
                prepareRow(row);
                return (
                  <tr {...row.getRowProps()} className="hover:bg-gray-50">
                    {row.cells.map(cell => (
                      <td
                        {...cell.getCellProps()}
                        className="px-6 py-4 whitespace-nowrap text-sm"
                      >
                        {cell.render('Cell')}
                      </td>
                    ))}
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={columns.length} className="px-6 py-4 text-center text-sm text-gray-500">
                  No alerts found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
} 